// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BeerCard from './BeerCard';
import useBeerStyles from '../../hooks/useBeerStyles';
import {
  mapIncomingBeerToCardBeer,
  mapMatchedBeerToCardBeer,
} from '../../utils/beer/mapBeerDuplicateReviewToCardBeer';

const DUPLICATE_FIELD_OPTIONS = [
  { key: 'name', label: 'Name' },
  { key: 'abv', label: 'ABV' },
  { key: 'style', label: 'Style' },
  { key: 'description', label: 'Description' },
];

function DuplicateFieldCheckbox({
  fieldKey,
  label,
  checked,
  disabled,
  onToggle,
}) {
  const handleChange = useCallback(() => {
    onToggle(fieldKey);
  }, [fieldKey, onToggle]);

  return (
    <FormControlLabel
      control={(
        <Checkbox
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
        />
      )}
      label={label}
    />
  );
}

DuplicateFieldCheckbox.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function buildStyleMap(beerStyles) {
  if (!Array.isArray(beerStyles)) {
    return {};
  }
  return beerStyles.reduce((accumulator, styleRow) => {
    if (styleRow?.id && styleRow?.name) {
      accumulator[styleRow.id] = styleRow.name;
    }
    return accumulator;
  }, {});
}

function formatConfidence(confidence) {
  if (confidence == null || confidence === '') {
    return '—';
  }
  return `${confidence}%`;
}

function BeerDuplicateCompareViewBody({
  reviewRow,
  actionsDisabled,
  onApplySelected,
  onCompleteReview,
  onAddBeer,
}) {
  const { beerStyles } = useBeerStyles();
  const styleMap = useMemo(() => buildStyleMap(beerStyles), [beerStyles]);
  const [selectedFields, setSelectedFields] = useState([]);

  const matchedCardBeer = useMemo(
    () => mapMatchedBeerToCardBeer(reviewRow, styleMap),
    [reviewRow, styleMap],
  );
  const incomingCardBeer = useMemo(
    () => mapIncomingBeerToCardBeer(reviewRow, styleMap),
    [reviewRow, styleMap],
  );

  const handleFieldToggle = useCallback((fieldKey) => {
    setSelectedFields((current) => {
      if (current.includes(fieldKey)) {
        return current.filter((entry) => entry !== fieldKey);
      }
      return [...current, fieldKey];
    });
  }, []);

  const handleApplyClick = useCallback(async () => {
    if (selectedFields.length === 0) {
      return;
    }
    const result = await onApplySelected(selectedFields);
    if (result?.success) {
      setSelectedFields([]);
    }
  }, [onApplySelected, selectedFields]);

  const handleCompleteClick = useCallback(() => {
    onCompleteReview();
  }, [onCompleteReview]);

  const handleAddBeerClick = useCallback(() => {
    onAddBeer();
  }, [onAddBeer]);

  const applyDisabled = actionsDisabled || selectedFields.length === 0;

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        EntiTea confidence: {formatConfidence(reviewRow?.confidence)}
        {reviewRow?.rationale ? ` — ${reviewRow.rationale}` : ''}
      </Typography>
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Existing beer
          </Typography>
          <BeerCard beer={matchedCardBeer} styleMap={styleMap} hideRatings />
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Incoming beer
          </Typography>
          <BeerCard beer={incomingCardBeer} styleMap={styleMap} hideRatings />
        </Grid>
        <Grid item xs={12} md={2}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Apply fields
          </Typography>
          <FormGroup>
            {DUPLICATE_FIELD_OPTIONS.map((option) => (
              <DuplicateFieldCheckbox
                key={option.key}
                fieldKey={option.key}
                label={option.label}
                checked={selectedFields.includes(option.key)}
                disabled={actionsDisabled}
                onToggle={handleFieldToggle}
              />
            ))}
          </FormGroup>
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="contained"
          onClick={handleApplyClick}
          disabled={applyDisabled}
        >
          Apply selected changes
        </Button>
        <Button
          variant="outlined"
          onClick={handleCompleteClick}
          disabled={actionsDisabled}
        >
          Complete Review
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAddBeerClick}
          disabled={actionsDisabled}
        >
          Add beer
        </Button>
      </Box>
    </Stack>
  );
}

BeerDuplicateCompareViewBody.propTypes = {
  reviewRow: PropTypes.object.isRequired,
  actionsDisabled: PropTypes.bool,
  onApplySelected: PropTypes.func.isRequired,
  onCompleteReview: PropTypes.func.isRequired,
  onAddBeer: PropTypes.func.isRequired,
};

BeerDuplicateCompareViewBody.defaultProps = {
  actionsDisabled: false,
};

function BeerDuplicateCompareView(props) {
  const reviewKey = props.reviewRow?.id ?? 'unknown-review';
  return <BeerDuplicateCompareViewBody key={reviewKey} {...props} />;
}

BeerDuplicateCompareView.propTypes = {
  reviewRow: PropTypes.object.isRequired,
  actionsDisabled: PropTypes.bool,
  onApplySelected: PropTypes.func.isRequired,
  onCompleteReview: PropTypes.func.isRequired,
  onAddBeer: PropTypes.func.isRequired,
};

BeerDuplicateCompareView.defaultProps = {
  actionsDisabled: false,
};

export default BeerDuplicateCompareView;
